/*                                                    */
import { DESTROYED, STALE_REACTION } from '#client/constants';
import { DEV } from 'esm-env';
import {
	component_context,
	dev_stack,
	is_runes,
	set_component_context,
	set_dev_stack
} from '../context.js';
import { Boundary } from '../dom/blocks/boundary.js';
import { invoke_error_boundary } from '../error-handling.js';
import {
	active_effect,
	active_reaction,
	set_active_effect,
	set_active_reaction
} from '../runtime.js';
import { Batch, current_batch } from './batch.js';
import {
	async_derived,
	reactivity_loss_tracker,
	derived,
	derived_safe_equal,
	set_reactivity_loss_tracker
} from './deriveds.js';
import { aborted } from './effects.js';

/**
 *
 *
 *
 *
 */
export function flatten(blockers, sync, async, fn) {
	const d = is_runes() ? derived : derived_safe_equal;

	/*                                                             */
	var pending = blockers.filter((b) => !b.settled);

	if (async.length === 0 && pending.length === 0) {
		fn(sync.map(d));
		return;
	}

	var parent = /*                 */ (active_effect);

	var restore = capture();
	var blocker_promise =
		pending.length === 1
			? pending[0].promise
			: pending.length > 1
				? Promise.all(pending.map((b) => b.promise))
				: null;

	/*                          */
	function finish(values) {
		restore();

		try {
			fn(values);
		} catch (error) {
			if ((parent.f & DESTROYED) === 0) {
				invoke_error_boundary(error, parent);
			}
		}

		unset_context();
	}

	/*                                           */
	if (async.length === 0) {
		/*                       */ (blocker_promise).then(() => finish(sync.map(d)));
		return;
	}

	var decrement_pending = increment_pending();

	/*                               */
	function run() {
		Promise.all(async.map((expression) => async_derived(expression)))
			.then((result) => finish([...sync.map(d), ...result]))
			.catch((error) => invoke_error_boundary(error, parent))
			.finally(() => decrement_pending());
	}

	if (blocker_promise) {
		blocker_promise.then(() => {
			restore();
			run();
			unset_context();
		});
	} else {
		run();
	}
}

/**
 *
 *
 */
export function run_after_blockers(blockers, fn) {
	flatten(blockers, [], [], fn);
}

/**
 *
 *
 *
 */
export function capture() {
	var previous_effect = /*                 */ (active_effect);
	var previous_reaction = active_reaction;
	var previous_component_context = component_context;
	var previous_batch = /*                */ (current_batch);

	if (DEV) {
		var previous_dev_stack = dev_stack;
	}

	return function restore(activate_batch = true) {
		set_active_effect(previous_effect);
		set_active_reaction(previous_reaction);
		set_component_context(previous_component_context);

		if (activate_batch && (previous_effect.f & DESTROYED) === 0) {
			/*                                                                     */
			/*                                                      */
			previous_batch?.activate();
			previous_batch?.apply();
		}

		if (DEV) {
			set_reactivity_loss_tracker(null);
			set_dev_stack(previous_dev_stack);
		}
	};
}

/**
 *
 *
 *
 *
 *
 *
 */
export async function save(promise) {
	var restore = capture();
	var value = await promise;

	return () => {
		restore();
		return value;
	};
}

/**
 *
 *
 *
 *
 *
 */
export async function track_reactivity_loss(promise) {
	var previous_async_effect = reactivity_loss_tracker;
	var value = await promise;

	return () => {
		set_reactivity_loss_tracker(previous_async_effect);
		return value;
	};
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 */
export async function* for_await_track_reactivity_loss(iterable) {
	/*                                                     */
	/*                     */
	/*                                                                                                                                                                         */
	/*                 */
	/*                                                                                */

	/*                                    */
	/*         */
	const iterator = iterable[Symbol.asyncIterator]?.() ?? iterable[Symbol.iterator]?.();

	if (iterator === undefined) {
		throw new TypeError('value is not async iterable');
	}

	/*                                                                                                               */
	let normal_completion = false;
	try {
		while (true) {
			const { done, value } = (await track_reactivity_loss(iterator.next()))();
			if (done) {
				normal_completion = true;
				break;
			}
			yield value;
		}
	} finally {
		/*                                                                                                            */
		if (normal_completion && iterator.return !== undefined) {
			/*                                         */
			return /*                  */ ((await track_reactivity_loss(iterator.return()))().value);
		}
	}
}

export function unset_context(deactivate_batch = true) {
	set_active_effect(null);
	set_active_reaction(null);
	set_component_context(null);
	if (deactivate_batch) current_batch?.deactivate();

	if (DEV) {
		set_reactivity_loss_tracker(null);
		set_dev_stack(null);
	}
}

/**
 *
 */
export function run(thunks) {
	const restore = capture();

	const decrement_pending = increment_pending();

	var active = /*                 */ (active_effect);

	/*                                */
	var errored = null;

	/*                     */
	const handle_error = (error) => {
		errored = { error }; /*                                                          */

		if (!aborted(active)) {
			invoke_error_boundary(error, active);
		}
	};

	var promise = Promise.resolve(thunks[0]()).catch(handle_error);

	/*                  */
	var blocker = { promise, settled: false };
	var blockers = [blocker];

	promise.finally(() => {
		blocker.settled = true;
		unset_context();
	});

	for (const fn of thunks.slice(1)) {
		promise = promise
			.then(() => {
				if (errored) {
					throw errored.error;
				}

				if (aborted(active)) {
					throw STALE_REACTION;
				}

				restore();
				return fn();
			})
			.catch(handle_error);

		const blocker = { promise, settled: false };
		blockers.push(blocker);

		promise.finally(() => {
			blocker.settled = true;
			unset_context();
		});
	}

	promise
		/*                                               */
		/*                                      */
		.then(() => Promise.resolve())
		.finally(() => decrement_pending());

	return blockers;
}

/**
 *
 */
export function wait(blockers) {
	return Promise.all(blockers.map((b) => b.promise));
}

/**
 *
 */
export function increment_pending() {
	var boundary = /*                   */ (/*                 */ (active_effect).b);
	var batch = /*                */ (current_batch);
	var blocking = boundary.is_rendered();

	boundary.update_pending_count(1, batch);
	batch.increment(blocking);

	return (skip = false) => {
		boundary.update_pending_count(-1, batch);
		batch.decrement(blocking, skip);
	};
}
